import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {
  ChatProposal,
  ChatRole,
  ProposalStatus,
} from '../../api/agentChat/interface';
import type { DossierEvent } from '../../api/events/interface';

dayjs.extend(utc);

export type DisplayRole = ChatRole | 'SYSTEM';

export interface DisplayMessage {
  message_id: string;
  role: DisplayRole;
  text: string;
  created_at?: string;
  streaming?: boolean;
  proposals?: ChatProposal[];
  artifact_id?: string | null;
  artifact_name?: string | null;
}

// Statuses that render a ProposalCard in the message thread.
export const TERMINAL_PROPOSAL_STATUSES: ProposalStatus[] = [
  'approved',
  'executing',
  'executed',
  'failed',
  'rejected',
  'cancelled',
];

// Statuses that cannot be overwritten by a later event notification.
export const IMMUTABLE_PROPOSAL_STATUSES: ProposalStatus[] = [
  'executed',
  'failed',
  'rejected',
  'cancelled',
];

export function hasPendingProposals(proposals?: ChatProposal[]): boolean {
  if (!proposals || proposals.length === 0) {
    return false;
  }
  return proposals.some((p) => !TERMINAL_PROPOSAL_STATUSES.includes(p.status));
}

export function getDateKey(isoString?: string): string | null {
  if (!isoString) {
    return null;
  }
  return dayjs.utc(isoString).local().format('YYYY-MM-DD');
}

export function formatDateLabel(dateKey: string): string {
  const today = dayjs().format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  if (dateKey === today) {
    return 'Today';
  }
  if (dateKey === yesterday) {
    return 'Yesterday';
  }
  return dayjs(dateKey).format('MMM D, YYYY');
}

// Walks `displayMessages` and replaces any `approved` proposals whose
// corresponding `user_directive_received` event has terminal status with the
// matching display state (executing / executed / failed).
export function reconcileProposals(
  displayMessages: DisplayMessage[],
  events: DossierEvent[]
): DisplayMessage[] {
  return displayMessages.map((m) => {
    if (!m.proposals?.some((p) => p.status === 'approved')) {
      return m;
    }
    return {
      ...m,
      proposals: m.proposals!.map((p) => {
        if (p.status !== 'approved') {
          return p;
        }
        const event = events.find((e) => {
          const md = e.event_metadata ?? {};
          return (
            e.event_type === 'user_directive_received' &&
            (md.proposal_id as string) === p.proposal_id
          );
        });
        if (!event) {
          return p;
        }
        const md = event.event_metadata ?? {};
        if (event.status === 'completed') {
          return {
            ...p,
            status: 'executed' as ProposalStatus,
            result: (md.expected_effects as string) ?? 'Done',
          };
        }
        if (event.status === 'error') {
          return {
            ...p,
            status: 'failed' as ProposalStatus,
            error: event.error_message,
          };
        }
        if (event.status === 'processing') {
          return { ...p, status: 'executing' as ProposalStatus };
        }
        return p;
      }),
    };
  });
}
